import { useState, useCallback, useEffect, useRef } from 'preact/hooks'
import type { Message, SessionState, CloudToolResult, GeneratedContent, EmailReviewRequest, ChatAttachment, ModelAccessEvent } from '../types'
import { startChat, stopChat, stopChatBeacon, sendMessage as apiSendMessage, updateMemory } from '../services/api'
import { getFallbackModel } from '../services/clientConfig'

/** generateId() is unavailable in non-secure contexts (HTTP over non-localhost). */
function generateId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
}

/**
 * Strip backend-injected envelope from a stored user message:
 *   - optional "VC_START:" short-chat marker
 *   - optional "[user time: ... yyyy HH:MM]" timestamp prepended by
 *     chat.py / realtime.py before persistence
 * Returns the inner content (everything after the envelope), trimmed.
 *
 * Mobile only sees these on the wire; web sees them from
 * cloud_chat_messages because that's how the backend wrote them.
 */
function stripMessageEnvelope(content: string): string {
  if (!content) return content
  let s = content
  if (s.startsWith('VC_START:')) s = s.slice('VC_START:'.length)
  // Backend's format_user_timestamp() emits "[user time: <date> HH:MM]"
  // followed by a single space. Drop that whole bracketed prefix.
  const tsMatch = /^\[user time:[^\]]*\]\s*/.exec(s)
  if (tsMatch) s = s.slice(tsMatch[0].length)
  return s.trimStart()
}

/**
 * Filter out system-generated voice call initiation messages that shouldn't
 * be displayed in the chat history UI. These are AI-initiated context blocks
 * (follow-ups, proactive insights, morning briefings, etc.) the mobile client
 * sends with metadata.hidden=true; on web we don't have that metadata in
 * cloud_chat_messages, so we detect by content prefix instead. Mirrors how
 * `text_chat_screen.dart` filters via `message.metadata?['hidden'] == true`.
 *
 * Stored messages carry the backend's timestamp envelope, so we strip that
 * first before pattern matching.
 */
function isVoiceCallSystemMessage(content: string): boolean {
  if (!content) return false
  const inner = stripMessageEnvelope(content)
  // "SEND_MSG:" — voice call system marker (after VC_START strip)
  if (inner.startsWith('SEND_MSG:')) return true
  // "REASON FOR CALL:" — server-generated follow-up / scheduled / proactive
  // call context block (see backend/config/prompts/call_prompts.json)
  if (inner.startsWith('REASON FOR CALL:')) return true
  // "[Scheduled call: <type>]" — Flutter placeholder pushed via
  // pipar_realtime_service._addHiddenUserMessage when using server-side
  // prompt generation (see pipar_realtime_service.dart:2796).
  if (inner.startsWith('[Scheduled call:')) return true
  return false
}

/**
 * Strip the "VC_START:" prefix from voice-call user messages,
 * leaving just the actual user speech text.
 */
function stripVoiceCallPrefix(content: string): string {
  if (!content) return content
  if (content.startsWith('VC_START:')) {
    return content.slice('VC_START:'.length).trim()
  }
  return content
}

/**
 * Pending session: messages are loaded for display, but the backend session
 * hasn't been created yet. It will be created lazily on the first sendMessage.
 */
interface PendingSession {
  personaId: string
  discussionId?: string
  existingMessages?: Array<Record<string, string>>
}

interface UseChatResult {
  messages: Message[]
  session: SessionState | null
  isLoading: boolean
  /** True when a backend session is live */
  isSessionActive: boolean
  /** True when messages are displayed (active session OR loaded history) */
  hasChatView: boolean
  /** Cloud tool results received during the current session (for triggering view refreshes) */
  toolResults: CloudToolResult[]
  /** Generated content pending user review (document or image preview) */
  generatedContent: GeneratedContent | null
  /** Dismiss generated content preview (user saved or discarded) */
  clearGeneratedContent: () => void
  /** Outbound email prepared by AI, awaiting user approval before send */
  pendingEmailReview: EmailReviewRequest | null
  /** Dismiss pending email review (user sent, edited+sent, or discarded) */
  clearPendingEmailReview: () => void
  /** Push the user-selected chat model into the hook. Should be called via
   *  useEffect whenever the caller's chatModel state changes; the hook then
   *  forwards this model to /voice_chat/start on subsequent session creates
   *  so Gemini explicit caching can fire for the right model. */
  setChatModel: (model: string | undefined) => void
  startSession: (personaId: string) => Promise<void>
  /** Set up an empty chat view without creating a backend session. The
   *  session is created lazily on the first sendMessage. Used by the
   *  "New Chat" button so the user doesn't burn a backend session if
   *  they open the chat view and walk away. */
  prepareNewChat: (personaId: string) => void
  loadHistory: (personaId: string, existingMessages: Array<Record<string, string>>, discussionId: string) => void
  sendMessage: (text: string, model?: string, attachments?: ChatAttachment[]) => Promise<void>
  /** Replace a user message with new content, drop everything after it,
   *  and resend through a fresh backend session. */
  editMessageAndResend: (messageId: string, newContent: string) => Promise<void>
  /** Drop a message and all newer messages; the next sendMessage will
   *  start a fresh backend session seeded with the truncated history. */
  deleteMessageAndNewer: (messageId: string) => Promise<void>
  stopSession: () => Promise<void>
  error: string | null
  /** Set when /chat refused the model for this subscription tier; carries the
   *  model to fall back to. Null once handled. */
  lastModelAccessDenial: ModelAccessEvent | null
  clearModelAccessDenial: () => void
}

export function useChat(): UseChatResult {
  const [messages, setMessages] = useState<Message[]>([])
  const [session, setSession] = useState<SessionState | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [toolResults, setToolResults] = useState<CloudToolResult[]>([])
  const [generatedContent, setGeneratedContent] = useState<GeneratedContent | null>(null)
  const [pendingEmailReview, setPendingEmailReview] = useState<EmailReviewRequest | null>(null)
  // The last `model_access` refusal, so the app can re-point the persona to
  // `fallback_model` and show the upgrade path. Sticky until cleared: the
  // refusal ends the stream, so there is nothing else carrying it.
  const [lastModelAccessDenial, setLastModelAccessDenial] = useState<ModelAccessEvent | null>(null)
  const pendingRef = useRef<PendingSession | null>(null)

  // Caller pushes the user-selected chat model into this ref via setChatModel
  // (typically from a useEffect that watches the model state). The ref is
  // read by every internal startChat call so /voice_chat/start gets the same
  // model the user will actually chat with — required for Gemini explicit
  // caching to fire (cache is keyed to the model).
  const chatModelRef = useRef<string | undefined>(undefined)
  const setChatModel = useCallback((model: string | undefined) => {
    chatModelRef.current = model
  }, [])

  // Fire /voice_chat/stop on tab close so the backend persists messages
  // to cloud_chat_messages before the session goes idle in Redis. Without
  // this, mobile (and re-loaded web tabs) see the cloud session row but
  // empty messages until the session expires.
  //
  // sessionRef is also used by ensureSession() to read the current session
  // synchronously — async flows that stop & restart the session (edit /
  // delete) need the latest value before React's next render.
  const sessionRef = useRef<SessionState | null>(null)
  const setSessionSync = useCallback((s: SessionState | null) => {
    sessionRef.current = s
    setSession(s)
  }, [])
  useEffect(() => {
    const onUnload = () => {
      const s = sessionRef.current
      if (s?.voiceChatId) {
        stopChatBeacon(s.voiceChatId, s.personaId)
      }
    }
    window.addEventListener('pagehide', onUnload)
    return () => window.removeEventListener('pagehide', onUnload)
  }, [])

  /**
   * Start a fresh backend session immediately. Used by private mode's
   * auto-start where there's nothing to wait for. The interactive New
   * Chat button uses prepareNewChat() instead so the backend session
   * isn't created until the user actually sends a message.
   */
  const startSession = useCallback(async (personaId: string) => {
    setError(null)
    setIsLoading(true)
    setToolResults([])
    pendingRef.current = null
    try {
      const res = await startChat({ personaId, model: chatModelRef.current })

      setSessionSync({
        voiceChatId: res.voice_chat_id,
        discussionId: `cloud_${Date.now()}`,
        personaId,
        storageMode: 'cloud',
      })
      setMessages([])
    } catch (e) {
      setError(String(e))
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Set up an empty chat view without creating a backend session.
   * ensureSession() will create the backend session on the first
   * sendMessage. Mirrors loadHistory() but with no prior messages.
   */
  const prepareNewChat = useCallback((personaId: string) => {
    setError(null)
    setToolResults([])
    setSessionSync(null)
    setMessages([])
    pendingRef.current = { personaId }
  }, [setSessionSync])

  /**
   * Load historical messages for display WITHOUT creating a backend session.
   * The session will be created lazily when the user sends a message.
   */
  const loadHistory = useCallback((personaId: string, existingMessages: Array<Record<string, string>>, discussionId: string) => {
    pendingRef.current = { personaId, discussionId, existingMessages }
    // Clear any active session reference (don't call stop — caller handles that)
    setSessionSync(null)
    setError(null)

    const loaded: Message[] = existingMessages
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .filter((m) => !isVoiceCallSystemMessage(m.content))
      .map((m, i) => ({
        id: `loaded_${i}`,
        role: m.role as 'user' | 'assistant',
        content: stripVoiceCallPrefix(m.content),
        timestamp: Date.now() - (existingMessages.length - i) * 1000,
      }))
    setMessages(loaded)
  }, [setSessionSync])

  /**
   * Ensure a backend session exists — create one if we're in pending state.
   * Reads sessionRef synchronously so flows that just stopped & nulled the
   * session (edit / delete) see the latest value before the next render.
   */
  const ensureSession = useCallback(async (): Promise<SessionState | null> => {
    // Already have an active session
    if (sessionRef.current) return sessionRef.current

    // Create from pending
    const pending = pendingRef.current
    if (!pending) return null

    const res = await startChat({
      personaId: pending.personaId,
      discussionId: pending.discussionId,
      messages: pending.existingMessages,
      model: chatModelRef.current,
    })

    const newSession: SessionState = {
      voiceChatId: res.voice_chat_id,
      discussionId: pending.discussionId || `cloud_${Date.now()}`,
      personaId: pending.personaId,
      storageMode: 'cloud',
    }
    setSessionSync(newSession)
    pendingRef.current = null
    return newSession
  }, [setSessionSync])

  /**
   * Stream a message into an already-resolved active session. Factored out
   * of sendMessage so flows that just stopped & restarted the session
   * (editMessageAndResend) can reuse the streaming logic without going
   * through ensureSession's stale-closure path.
   */
  const streamThroughSession = useCallback(async (
    activeSession: SessionState,
    trimmed: string,
    model?: string,
    attachments?: ChatAttachment[],
  ) => {
    setIsLoading(true)
    setError(null)

    const userMsg: Message = {
      id: generateId(),
      role: 'user',
      content: trimmed,
      timestamp: Date.now(),
      attachments: attachments && attachments.length > 0 ? attachments : undefined,
    }
    setMessages((prev) => [...prev, userMsg])

    const assistantId = generateId()
    const assistantMsg: Message = {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: Date.now(),
      isStreaming: true,
    }
    setMessages((prev) => [...prev, assistantMsg])

    try {
      // Stream the /chat response. The backend auto-continues when server-side
      // cloud tools (read_context, write_context, search_memory) are executed,
      // so the client only receives text chunks and optional tool_progress events.
      // For local storage mode, tool_call/tool_executed events are still forwarded.
      for await (const event of apiSendMessage(activeSession.voiceChatId, trimmed, model, attachments)) {
        switch (event.type) {
          case 'text':
          case 'content':
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? { ...m, content: m.content + (typeof event.data === 'string' ? event.data : '') }
                  : m,
              ),
            )
            break

          case 'tool_progress': {
            // Show transient progress indicator — will be removed when AI responds
            const progressData = event.data as Record<string, unknown> | undefined
            const progressId = `toolprogress_${assistantId}`
            const content = String(progressData?.status || 'Processing...')
            setMessages((prev) => {
              // Replace existing progress message or insert new one
              const existingIdx = prev.findIndex((m) => m.id === progressId)
              if (existingIdx >= 0) {
                return prev.map((m) => m.id === progressId ? { ...m, content } : m)
              }
              const idx = prev.findIndex((m) => m.id === assistantId)
              const progressMsg: Message = {
                id: progressId,
                role: 'system',
                content,
                timestamp: Date.now(),
                toolName: String(progressData?.tool || ''),
              }
              if (idx >= 0) {
                const copy = [...prev]
                copy.splice(idx, 0, progressMsg)
                return copy
              }
              return [...prev, progressMsg]
            })
            break
          }

          case 'tool_executed':
            // Legacy event — ignore silently (AI response describes the result)
            break

          case 'cloud_tool_result': {
            // Track result for view refreshes, but don't show in chat
            // (the AI's text response already describes what happened)
            const resultData = event.data as Record<string, unknown> | undefined
            if (resultData) {
              setToolResults((prev) => [...prev, {
                tool: String(resultData.tool || ''),
                action: resultData.action as CloudToolResult['action'],
                entity_type: resultData.entity_type as CloudToolResult['entity_type'],
                entity_id: String(resultData.entity_id || ''),
                entity_data: (resultData.entity_data as Record<string, unknown>) || null,
              }])
              // Remove tool progress indicator since tool completed
              const progressId = `toolprogress_${assistantId}`
              setMessages((prev) => prev.filter((m) => m.id !== progressId))
            }
            break
          }

          case 'generated_content': {
            // AI generated a document or image — show preview for user to save/discard
            const genData = event.data as unknown as GeneratedContent | undefined
            if (genData) {
              setGeneratedContent(genData)
            }
            break
          }

          case 'email_review_required': {
            // AI prepared an outbound email to an external recipient — show
            // approval dialog. On approval, UI POSTs to /contacts/send_message.
            const reviewData = event.data as unknown as EmailReviewRequest | undefined
            if (reviewData) {
              setPendingEmailReview(reviewData)
              // Remove any transient tool progress indicator
              const progressId = `toolprogress_${assistantId}`
              setMessages((prev) => prev.filter((m) => m.id !== progressId))
            }
            break
          }

          case 'guard_active': {
            // The free-model allowance ran out. Deliberately NOT an error: the
            // user has almost certainly got credits and simply reached the
            // window, so the next turn is metered rather than refused. The
            // backend follows this frame with a plain `error` one for clients
            // that don't know the type — we stop here, so that never renders.
            const resetsIn = event.resets_in_seconds
            const perMessage = event.continue_on_credits?.credits_per_message_estimate
            const parts: string[] = [
              event.error ||
                (event.guard === 'weekly'
                  ? "You've used your free messages for this week."
                  : "You've used your free messages for now."),
            ]
            if (typeof resetsIn === 'number' && resetsIn > 0) {
              const hours = Math.round(resetsIn / 3600)
              parts.push(
                hours >= 1
                  ? `Resets in about ${hours} hour${hours === 1 ? '' : 's'}.`
                  : 'Resets shortly.',
              )
            }
            if (event.continue_on_credits?.available) {
              parts.push(
                typeof perMessage === 'number'
                  ? `Sending again continues on credits — about ${perMessage.toFixed(1)} a message.`
                  : 'Sending again continues on credits.',
              )
            }
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId ? { ...m, content: parts.join(' '), isStreaming: false } : m,
              ),
            )
            setIsLoading(false)
            throw new Error('__handled_error__')
          }

          case 'model_access': {
            // The model isn't in this subscription tier — a lapsed Premium
            // subscription whose persona still points at Claude, or a stale
            // stored selection. The server names the model to fall back to;
            // surface it so the user knows what to switch to rather than
            // leaving them on a model that fails on every message.
            // The server names the model to fall back to; getFallbackModel()
            // is the same value from /client/config, and 'pipar-basic' is the
            // last resort — it must match `fallback_model` in the backend's
            // model_access.yaml, because a client that guessed a stronger star
            // would re-point the persona onto a model the server also refuses.
            const fallback = event.fallback_model || getFallbackModel() || 'pipar-basic'
            setLastModelAccessDenial({
              model: event.model,
              tier: event.tier,
              fallback_model: fallback,
              upgrade_tiers: event.upgrade_tiers,
              error: event.error,
            })
            // Our own sentence rather than `event.error`: the server's copy
            // ends with "Pick a PiPar model", which is stale advice here —
            // app.tsx re-points the persona to `fallback_model` on this same
            // event, so by the time the user reads it, it is already done.
            const denied = event.model ? `“${event.model}”` : 'That model'
            const notice =
              `${denied} is not included in your subscription. ` +
              `Switched to ${fallback} — send your message again to continue.` +
              (event.upgrade_tiers?.length
                ? ' Premium includes the full model catalogue.'
                : '')
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId ? { ...m, content: notice, isStreaming: false } : m,
              ),
            )
            setIsLoading(false)
            throw new Error('__handled_error__')
          }

          case 'error':
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? { ...m, content: typeof event.data === 'string' ? event.data : 'An error occurred.', isStreaming: false }
                  : m,
              ),
            )
            setIsLoading(false)
            throw new Error('__handled_error__')
        }
      }

      // Finalize: remove tool progress indicator and finalize assistant placeholder
      const progressId = `toolprogress_${assistantId}`
      setMessages((prev) => {
        const cleaned = prev.filter((m) => m.id !== progressId)
        const msg = cleaned.find((m) => m.id === assistantId)
        if (!msg) return cleaned
        if (!msg.content.trim()) return cleaned.filter((m) => m.id !== assistantId)
        return cleaned.map((m) => (m.id === assistantId ? { ...m, isStreaming: false } : m))
      })
    } catch (e) {
      // Error events from the stream are already handled above
      if (String(e).includes('__handled_error__')) return
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId
            ? { ...m, content: `Network error: ${e}`, isStreaming: false }
            : m,
        ),
      )
    } finally {
      setIsLoading(false)
    }
  }, [])

  const sendMessage = useCallback(async (
    text: string,
    model?: string,
    attachments?: ChatAttachment[],
  ) => {
    if (isLoading) return
    const trimmed = text.trim()
    const hasAttachments = !!attachments && attachments.length > 0
    if (!trimmed && !hasAttachments) return

    setError(null)

    // Ensure backend session exists (lazy create for loaded history)
    let activeSession: SessionState | null
    try {
      activeSession = await ensureSession()
    } catch (e) {
      setError(`Failed to start session: ${e}`)
      return
    }
    if (!activeSession) {
      setError('No active session')
      return
    }

    await streamThroughSession(activeSession, trimmed, model, attachments)
  }, [isLoading, ensureSession, streamThroughSession])

  /**
   * Replace a user message with newContent, drop everything after it, and
   * resend through a fresh backend session seeded with the truncated
   * history. Mirrors `_editMessageAndResend` in text_chat_screen.dart.
   */
  const editMessageAndResend = useCallback(async (messageId: string, newContent: string) => {
    const trimmed = newContent.trim()
    if (!trimmed) return

    // Capture truncated history synchronously via the functional updater.
    let history: Array<{ role: string; content: string }> = []
    let found = false
    setMessages((prev) => {
      const idx = prev.findIndex((m) => m.id === messageId)
      if (idx < 0) return prev
      found = true
      const truncated = prev.slice(0, idx)
      history = truncated
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m) => ({ role: m.role, content: m.content }))
      return truncated
    })
    if (!found) return

    const cur = sessionRef.current
    const personaId = cur?.personaId || pendingRef.current?.personaId
    const discussionId = cur?.discussionId || pendingRef.current?.discussionId
    if (!personaId) return

    setIsLoading(true)
    if (cur) {
      try {
        await stopChat(cur.voiceChatId, cur.personaId)
      } catch (e) {
        console.warn('Failed to stop session before edit:', e)
      }
      setSessionSync(null)
    }

    let newSession: SessionState
    try {
      const res = await startChat({
        personaId,
        discussionId,
        messages: history,
        model: chatModelRef.current,
      })
      newSession = {
        voiceChatId: res.voice_chat_id,
        discussionId: discussionId || `cloud_${Date.now()}`,
        personaId,
        storageMode: 'cloud',
      }
      setSessionSync(newSession)
      pendingRef.current = null
    } catch (e) {
      setError(`Failed to restart session after edit: ${e}`)
      setIsLoading(false)
      return
    }
    setIsLoading(false)

    // Stream the edited message through the new session.
    await streamThroughSession(newSession, trimmed)
  }, [setSessionSync, streamThroughSession])

  /**
   * Drop a message and everything after it from the displayed history and
   * stop the active backend session. The next sendMessage will create a
   * new session via ensureSession with the truncated history. Mirrors
   * `_deleteMessageAndNewer` in text_chat_screen.dart.
   */
  const deleteMessageAndNewer = useCallback(async (messageId: string) => {
    let history: Array<{ role: string; content: string }> = []
    let found = false
    setMessages((prev) => {
      const idx = prev.findIndex((m) => m.id === messageId)
      if (idx < 0) return prev
      found = true
      const truncated = prev.slice(0, idx)
      history = truncated
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m) => ({ role: m.role, content: m.content }))
      return truncated
    })
    if (!found) return

    const cur = sessionRef.current
    const personaId = cur?.personaId || pendingRef.current?.personaId
    const discussionId = cur?.discussionId || pendingRef.current?.discussionId
    if (!personaId) return

    if (cur) {
      try {
        await stopChat(cur.voiceChatId, cur.personaId)
      } catch (e) {
        console.warn('Failed to stop session before delete:', e)
      }
      setSessionSync(null)
    }

    // Set up pending so the next sendMessage seeds the new session with the
    // truncated history. If the user immediately stops/leaves the chat, the
    // pending state is dropped on stopSession or unload.
    pendingRef.current = {
      personaId,
      discussionId,
      existingMessages: history,
    }
  }, [setSessionSync])

  const stopSession = useCallback(async () => {
    // If only viewing history (pending, no backend session), just clear state
    if (!session) {
      pendingRef.current = null
      setMessages([])
      return
    }

    setIsLoading(true)
    try {
      const stopRes = await stopChat(session.voiceChatId, session.personaId)

      // Trigger memory update in background
      const rawHistory = stopRes.messages || messages.map((m) => ({ role: m.role, content: m.content }))
      const history = rawHistory
        .filter((m: Record<string, unknown>) => m.role === 'user' || m.role === 'assistant')
        .map((m: Record<string, unknown>) => {
          let content = m.content
          if (Array.isArray(content)) {
            content = (content as Array<Record<string, string>>)
              .filter((b) => b.type === 'text')
              .map((b) => b.text)
              .join('\n')
          }
          return { role: m.role as string, content: (content as string) || '' }
        })
      if (history.length > 0) {
        updateMemory({
          discussionId: stopRes.discussion_id || session.discussionId,
          messageHistory: history,
          personaName: session.personaId,
          personaId: session.personaId,
        }).catch((e) => console.warn('Memory update failed:', e))
      }

      setSessionSync(null)
      pendingRef.current = null
      setMessages([])
    } catch (e) {
      setError(String(e))
    } finally {
      setIsLoading(false)
    }
  }, [session, messages])

  const clearModelAccessDenial = useCallback(() => setLastModelAccessDenial(null), [])
  const clearGeneratedContent = useCallback(() => setGeneratedContent(null), [])
  const clearPendingEmailReview = useCallback(() => setPendingEmailReview(null), [])

  return {
    messages,
    session,
    isLoading,
    isSessionActive: session !== null,
    hasChatView: session !== null || pendingRef.current !== null || messages.length > 0,
    toolResults,
    generatedContent,
    clearGeneratedContent,
    pendingEmailReview,
    clearPendingEmailReview,
    lastModelAccessDenial,
    clearModelAccessDenial,
    setChatModel,
    startSession,
    prepareNewChat,
    loadHistory,
    sendMessage,
    editMessageAndResend,
    deleteMessageAndNewer,
    stopSession,
    error,
  }
}
